import { useMemo } from 'react';
import { useUserMeLive } from '@/api/user/use-user-me-live';
import {
  canConnectNewBankAccounts,
  canUseTechnicianResource,
  type TechnicianResource,
  canImportBankStatements,
  canManageBalanceOnboarding,
  canManagePublicApiKeys,
  canUseReconciliation,
  canViewCashFlowForecast,
  canViewDscr,
  isTechnicianSeat,
} from '@repo/utils';

export function usePermissions() {
  const { data: user, isLoading } = useUserMeLive();
  const role = user?.tenantRole;
  const isOwner = role === 'OWNER';

  return useMemo(() => {
    const flags = {
      // Section visibility — OWNER always has all permissions
      canAccessDashboard: true,
      canAccessAccounts: isOwner || user?.canAccessAccounts || false,
      canAccessForecast: isOwner || user?.canAccessForecast || false,
      canAccessDscr: isOwner || user?.canAccessDscr || false,
      canAccessDocuments: isOwner || user?.canAccessDocuments || false,
      canAccessDeliveryNotes: isOwner || user?.canAccessDeliveryNotes || false,
      canAccessOrders: isOwner || user?.canAccessOrders || false,
      canAccessAdministration:
        isOwner || user?.canAccessAdministration || false,
      canAccessCommesse: isOwner || user?.canAccessCommesse || false,
      canAccessServiceContracts:
        isOwner || user?.canAccessServiceContracts || false,
      canAccessWorkReports: isOwner || user?.canAccessWorkReports || false,
      canAccessHotel: isOwner || user?.canAccessHotel || false,
      canAccessRegistry: isOwner || user?.canAccessRegistry || false,
      canAccessProducts: isOwner || user?.canAccessProducts || false,
      canAccessWarehouse: isOwner || user?.canAccessWarehouse || false,
      canAccessPaymentSchedules:
        isOwner || user?.canAccessPaymentSchedules || false,
      canAccessPayments: isOwner || user?.canAccessPayments || false,
      canAccessSettings: isOwner || user?.canAccessSettings || false,

      canReadWorkReportProducts:
        isOwner || user?.canReadWorkReportProducts || false,
      canCreateWorkReportProducts:
        isOwner || user?.canCreateWorkReportProducts || false,
      canCreateWorkReportCustomers:
        isOwner || user?.canCreateWorkReportCustomers || false,
      canWriteWorkReportMachines:
        isOwner || user?.canWriteWorkReportMachines || false,
      canReadWorkReportChecks:
        isOwner || user?.canReadWorkReportChecks || false,
      canWriteWorkReportChecks:
        isOwner || user?.canWriteWorkReportChecks || false,
      canReadWorkReportAttachments:
        isOwner || user?.canReadWorkReportAttachments || false,
      canWriteWorkReportAttachments:
        isOwner || user?.canWriteWorkReportAttachments || false,
      canReadWorkReportContracts:
        isOwner || user?.canReadWorkReportContracts || false,
      canReadWorkReportDeliveryNotes:
        isOwner || user?.canReadWorkReportDeliveryNotes || false,
      canReadAllWorkReports: isOwner || user?.canReadAllWorkReports || false,
      canViewWorkReportPrices:
        isOwner || user?.canViewWorkReportPrices || false,

      // Actions
      canWrite: isOwner || user?.canWrite || false,
      canExecutePayments: isOwner || user?.canExecutePayments || false,
      canManageUsers: isOwner || user?.canManageUsers || false,
      canConnectBankAccounts: isOwner || user?.canConnectBankAccounts || false,
      canConnectCassettoFiscale:
        isOwner || user?.canConnectCassettoFiscale || false,
      canExport: isOwner || user?.canExport || false,
      canManageBilling: isOwner || user?.canManageBilling || false,
      canReconcile: isOwner || user?.canReconcile || false,
      canUseAI: isOwner || user?.canUseAI || false,
      canConnectMcp: isOwner || user?.canConnectMcp || false,
      canConnectJetHr: isOwner || user?.canConnectJetHr || false,
      canConnectPaypal: isOwner || user?.canConnectPaypal || false,
      canConnectReviso: isOwner || user?.canConnectReviso || false,
      canManageApiKeys: isOwner || user?.canManageApiKeys || false,
      canApproveWorkReports: isOwner || user?.canApproveWorkReports || false,
      canViewAccountBalances: isOwner || user?.canViewAccountBalances || false,
      canApproveInvoices: isOwner || user?.canApproveInvoices || false,
      canAccessMachines: isOwner || user?.canAccessMachines || false,
      canApproveMachineChecks:
        isOwner || user?.canApproveMachineChecks || false,
    };

    // I flag operativi del posto tecnico aprono solo il lavoro sul campo:
    // archivio, eliminazione, trasferimento, import e piani restano chiusi
    // al tecnico come nel backend (`@TechnicianManagementOnly`).
    const technicianSeat = isTechnicianSeat({ role, flags });
    // Come `PermissionGuard`: il tecnico scrive con i flag operativi, gli
    // altri membri con canWrite.
    const canWriteResource = (resource: TechnicianResource) =>
      flags.canAccessMachines &&
      (technicianSeat || flags.canWrite) &&
      canUseTechnicianResource({ role, flags }, resource, true);
    const canWriteMachines = canWriteResource('machines');
    const canWriteMachineChecks = canWriteResource('checks');
    const canWriteMachineAttachments = canWriteResource('attachments');

    return {
      isLoading,
      ...flags,

      canWriteMachines,
      canManageMachines: canWriteMachines && !technicianSeat,
      canReadMachineChecks: canUseTechnicianResource({ role, flags }, 'checks'),
      canWriteMachineChecks,
      canManageMachineChecks: canWriteMachineChecks && !technicianSeat,
      canReadMachineAttachments: canUseTechnicianResource(
        { role, flags },
        'attachments'
      ),
      canWriteMachineAttachments,
      canDeleteMachineAttachments:
        canWriteMachineAttachments && !technicianSeat,

      // Predicati derivati condivisi col backend (@repo/utils): un'azione
      // è offerta solo se l'intera combinazione richiesta dal server è
      // presente (piano fix permessi, Fase 4).
      canUseReconciliation: canUseReconciliation(flags),
      canImportBankStatements: canImportBankStatements(flags),
      canConnectNewBankAccounts: canConnectNewBankAccounts(flags),
      canViewCashFlowForecast: canViewCashFlowForecast(flags),
      canViewDscr: canViewDscr(flags),
      canManageBalanceOnboarding: canManageBalanceOnboarding(flags),
      canManagePublicApiKeys: canManagePublicApiKeys(flags),

      // Role helpers
      isAdmin: role === 'OWNER' || role === 'ADMIN',
      isGuest: role === 'GUEST',
      isUser: role === 'USER',
      isOwner,
      role,
    };
  }, [user, isLoading, role, isOwner]);
}
